import { useEffect, useMemo, useState, useCallback } from 'react';
import { CardRecord } from '../db/indexedDb';
import {
  VocabPracticeCard,
  normalizeAnswerText,
  normalizeVocabMeta,
} from '../services/srsQueue/vocabQueue';
import { checkVocabAnswer, type VocabCheckResult } from '../services/llm/vocabCheck';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

type Props = {
  card: CardRecord;
};

const formatExamples = (examples: string[]) => examples.filter(Boolean).map((item, idx) => <li key={idx}>{item}</li>);

type CheckStatus = 'idle' | 'checking' | 'correct' | 'partial' | 'wrong';

const statusLabels: Record<CheckStatus, { text: string; tone: 'neutral' | 'success' | 'warn' | 'danger' }> = {
  idle: { text: '', tone: 'neutral' },
  checking: { text: 'LLM 检查中...', tone: 'neutral' },
  correct: { text: '✓ 掌握', tone: 'success' },
  partial: { text: '⚠ 部分掌握', tone: 'warn' },
  wrong: { text: '✗ 未掌握', tone: 'danger' },
};

const VocabFlipCard = ({ card }: Props) => {
  const practice = card as VocabPracticeCard;
  const practiceMode = practice.practiceMode ?? 'input';
  const practiceId = practice.practiceId ?? card.id;

  const normalized = useMemo(() => normalizeVocabMeta(card), [card]);
  const acceptedNormalized = useMemo(
    () => normalized.acceptableAnswers.map((item) => normalizeAnswerText(item)),
    [normalized.acceptableAnswers]
  );

  const [flipped, setFlipped] = useState(practiceMode === 'output');
  const [answer, setAnswer] = useState('');
  const [status, setStatus] = useState<CheckStatus>('idle');

  useEffect(() => {
    setFlipped(practiceMode === 'output');
    setAnswer('');
    setStatus('idle');
  }, [practiceId, practiceMode]);

  const normalizeUserAnswer = () => normalizeAnswerText(answer);

  const evaluate = async () => {
    const value = normalizeUserAnswer();
    
    // E→C 模式：本地检查或直接翻面
    if (practiceMode === 'input') {
      if (!value) {
        setFlipped(true);
        return;
      }
      const hit = acceptedNormalized.indexOf(value) >= 0;
      setStatus(hit ? 'correct' : 'wrong');
      setFlipped(true);
      return;
    }
    
    // C→E 模式：必须输入，用 LLM 检查
    if (!value) {
      setStatus('wrong');
      return;
    }
    
    // 先尝试本地精确匹配
    if (acceptedNormalized.indexOf(value) >= 0) {
      setStatus('correct');
      setFlipped(true);
      return;
    }
    
    // 调用 LLM 检查
    setStatus('checking');
    try {
      const res = await checkVocabAnswer(value, normalized.en, normalized.acceptableAnswers);
      const resultMap: Record<VocabCheckResult, CheckStatus> = {
        mastered: 'correct',
        partial: 'partial',
        not_mastered: 'wrong',
      };
      setStatus(resultMap[res.result]);
    } catch {
      setStatus('wrong');
    }
    setFlipped(true);
  };

  const hint = normalized.scene || normalized.examples[0];
  const statusMeta = statusLabels[status];

  const acceptedList = normalized.acceptableAnswers.length
    ? normalized.acceptableAnswers.join(' / ')
    : normalized.en || '暂无';

  // 键盘快捷键：Space 翻面
  const handleFlipCard = useCallback(() => {
    setFlipped((v) => !v);
  }, []);

  useKeyboardShortcuts({
    shortcuts: {
      ' ': handleFlipCard,  // Space = 翻面
      's': handleFlipCard,  // S = 翻面（skip/show）
    },
  });

  return (
    <div className="practice-card vocab-compact">
      {/* Compact header */}
      <div className="practice-card-header">
        <h3 className="practice-question">
          {practiceMode === 'input' ? normalized.en || '词汇' : normalized.cn || '释义'}
        </h3>
        <div className="practice-cues">
          <span className="pill">{practiceMode === 'input' ? 'E→C' : 'C→E'}</span>
          {normalized.important && <span className="pill">重要</span>}
        </div>
      </div>

      <div className={`flip-card ${flipped ? 'flipped' : ''}`}>
        <div className="flip-card-inner">
          <div className="flip-face flip-front">
            {practiceMode === 'input' ? (
              <>
                <p className="mega">{normalized.en || '未提供英文'}</p>
                {normalized.examples.length ? (
                  <ul className="vocab-examples">{formatExamples(normalized.examples)}</ul>
                ) : (
                  <p className="session-body muted">无例句，直接根据释义记忆。</p>
                )}
              </>
            ) : (
              <>
                <p className="mega">{normalized.cn || '未提供中文释义'}</p>
                {hint ? <p className="session-body">提示：{hint}</p> : null}
              </>
            )}
          </div>
          <div className="flip-face flip-back">
            {practiceMode === 'input' ? (
              <>
                <p className="mega">{normalized.cn || '未提供中文释义'}</p>
                {normalized.scene ? <p className="session-body">场景：{normalized.scene}</p> : null}
                <p className="session-meta">英文：{normalized.en || '未知'}</p>
              </>
            ) : (
              <>
                <p className="mega">{normalized.en || '未提供英文'}</p>
                {normalized.examples.length ? (
                  <ul className="vocab-examples">{formatExamples(normalized.examples)}</ul>
                ) : null}
                <p className="session-meta">可接受：{acceptedList}</p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="vocab-input-area">
        <div className="vocab-input-row">
          <input
            id={`vocab-answer-${practiceId}`}
            className="vocab-input"
            placeholder={practiceMode === 'output' ? '输入英文' : '可选输入'}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                evaluate();
              }
            }}
          />
          <button type="button" className="vocab-btn primary" onClick={evaluate}>检查 ↵</button>
          <button type="button" className="vocab-btn" onClick={() => setFlipped((v) => !v)}>翻面 (S)</button>
        </div>
        {status !== 'idle' && statusMeta.text && (
          <p className={`vocab-status ${statusMeta.tone}`}>{statusMeta.text}</p>
        )}
      </div>
    </div>
  );
};

export default VocabFlipCard;
